import React, { useMemo } from 'react'
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import { format, isValid, parseISO } from 'date-fns'

import { useSeasonEnrichmentAutomations } from '../hooks/useSeasonEnrichmentAutomations'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        marginTop: theme.spacing(2),
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexWrap: 'wrap',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    body: {
        padding: theme.spacing(2),
    },
    offBanner: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: theme.palette.text.secondary,
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        '& + &': {
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(2),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
    },
    meta: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        textAlign: 'right',
        whiteSpace: 'nowrap',
    },
    metaStrong: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    nextRun: {
        marginTop: theme.spacing(2),
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
}))

function formatRunAt(value: string | null | undefined): string {
    if (!value) return '—'
    const date = parseISO(value)
    if (!isValid(date)) return '—'
    return format(date, 'MMM d, yyyy h:mm a')
}

interface AutomationsCardProps {
    seasonId?: number | null
    readOnly?: boolean
    tokenBalance?: number | null
    enrichingPercent?: number
}

export const AutomationsCard: React.FC<AutomationsCardProps> = ({
    seasonId,
    readOnly = false,
    tokenBalance,
    enrichingPercent = 0,
}) => {
    const classes = useStyles()
    const {
        automations,
        isLoading,
        runNow,
        isRunning,
        isInFlight,
    } = useSeasonEnrichmentAutomations(seasonId)

    const nextRunAt = useMemo(() => {
        const times = automations
            .map((row) => row.next_run_at)
            .filter(Boolean)
            .sort()
        return times[0] ?? null
    }, [automations])

    const enrichingOn = enrichingPercent > 0
    const hasTokens = (tokenBalance ?? 0) > 0
    const canRun = !readOnly && enrichingOn && hasTokens && !isRunning

    return (
        <Card className={classes.card} variant="outlined">
            <Box className={classes.header}>
                <Box>
                    <Typography variant="subtitle1">
                        <strong>Prospect data enriching</strong>
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Email and phone share this slider&apos;s monthly token
                        budget. 0% turns enrichment off.
                    </Typography>
                </Box>
                <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    startIcon={
                        isRunning ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <PlayArrowIcon />
                        )
                    }
                    onClick={() => runNow()}
                    disabled={!canRun}
                >
                    {isInFlight || isRunning ? 'Enriching…' : 'Run now'}
                </Button>
            </Box>
            <Box className={classes.body}>
                {isLoading && automations.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                    </Box>
                ) : !enrichingOn ? (
                    <Typography className={classes.offBanner}>
                        Enriching is off. Move Prospect data enriching above 0%
                        to start using monthly tokens.
                    </Typography>
                ) : (
                    <>
                        {automations.map((row) => (
                            <Box key={row.id} className={classes.row}>
                                <Box>
                                    <Typography variant="body2">
                                        <strong>{row.title}</strong>
                                    </Typography>
                                    <Typography className={classes.subtitle}>
                                        {row.description}
                                    </Typography>
                                </Box>
                                <Typography className={classes.meta}>
                                    <span className={classes.metaStrong}>
                                        {row.est_tokens_per_month.toLocaleString()}
                                    </span>
                                    {' tok / mo'}
                                    <br />
                                    {row.token_cost_per_prospect} tok / person
                                </Typography>
                            </Box>
                        ))}
                        <Typography className={classes.nextRun}>
                            Next scheduled run:{' '}
                            <strong>{formatRunAt(nextRunAt)}</strong>
                            {!hasTokens &&
                                ' · Grant tokens to this property to run.'}
                            {hasTokens &&
                                isInFlight &&
                                ' · A run is in progress. Tokens deduct as each person is processed.'}
                            {hasTokens &&
                                !isInFlight &&
                                ' · Run now spends from the current balance, then the next run waits a month.'}
                        </Typography>
                    </>
                )}
            </Box>
        </Card>
    )
}
